import posthog from 'posthog-js';
import React from 'react';

import useAuth, {useCurrentOrganization} from 'src/hooks/useAuth';
import {isProd} from 'src/lib/env';
import {isUpstreamEmail} from 'src/lib/userUtils';

// Set to true for testing
const CAPTURE_ME_PLEASE = false;

const POSTHOG_KEY = 'phc_I3SlLyFTMjF9qrtX6HSxWjvtIxXVsFk4HxMTbtS51og';
const POSTHOG_HOST = 'https://us.i.posthog.com';

const Analytics: React.FunctionComponent = () => {
  const auth = useAuth();
  const currentOrganization = useCurrentOrganization();

  const profile = auth.status === 'logged in' ? auth.userProfile : null;
  const firebaseUid = auth.status === 'logged in' ? auth.firebaseUid : null;

  React.useEffect(() => {
    if (!profile || !currentOrganization || !firebaseUid) {
      return;
    }

    const fullCapture = (isProd && !isUpstreamEmail(profile.email)) || CAPTURE_ME_PLEASE;
    // Internal Upstream users are kept out of product analytics, but we still
    // want the dynamical direct-read correctness/latency telemetry from internal
    // dogfooding. Load a minimal, anonymous PostHog for them that captures ONLY
    // those events (see recordEvent's allowlist) — no autocapture, pageviews, or
    // person profiles — so product usage metrics stay clean.
    const internalTelemetryOnly =
      isProd && isUpstreamEmail(profile.email) && !CAPTURE_ME_PLEASE;

    if (fullCapture) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        autocapture: false,
      });

      posthog.identify(firebaseUid, {
        userId: profile.id,
        email: profile.email,
        role: profile.role,
        organizationId: currentOrganization.id,
        organization: currentOrganization.name,
        locale: profile.settings?.locale,
      });

      posthog.group('organization', currentOrganization.id, currentOrganization);

      internalMetricsOnly = false;
      return () => {
        posthog.reset();
      };
    }

    if (internalTelemetryOnly) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        person_profiles: 'never',
      });
      internalMetricsOnly = true;
      return () => {
        internalMetricsOnly = false;
        posthog.reset();
      };
    }
  }, [firebaseUid, profile, currentOrganization]);

  return null;
};

export default Analytics;

type EventProps = {[key: string]: string | number | boolean};

// Dynamical direct-read telemetry — the only events allowed through when
// PostHog is loaded in internal-telemetry-only mode (internal Upstream users),
// so their usage never lands in product analytics. See the Analytics effect.
const DYNAMICAL_TELEMETRY_EVENTS: ReadonlySet<Events> = new Set([
  'dynamical_shadow_parity',
  'dynamical_direct_fallback',
]);

let internalMetricsOnly = false;

export function recordEvent(name: Events, props?: EventProps): void {
  if (!posthog.__loaded) {
    return;
  }
  if (internalMetricsOnly && !DYNAMICAL_TELEMETRY_EVENTS.has(name)) {
    return;
  }
  posthog.capture(name, props);
}

type Events =
  | 'change_locale'
  | 'default_weather_display_changed'
  | 'legend_item_clicked'
  | 'legend_group_clicked'
  | 'legend_expanded'
  | 'forecast_selected'
  | 'forecast_shortcut_clicked'
  | 'download_csv_clicked'
  | 'download_image_clicked'
  | 'copy_image_clicked'
  | 'open_table_clicked'
  | 'date_range_modified'
  | 'generate_smart_summary_clicked'
  | 'basin_average_toggled'
  | 'seasonal_volume_mode_toggled'
  | 'seasonal_volume_period_modified'
  | 'seasonal_volume_threshold_added'
  | 'seasonal_volume_org_default_set'
  | 'notification_toggled'
  | 'aggregation_option_changed'
  | 'forecast_model_changed'
  | 'site_selected'
  | 'swe_analysis_clicked'
  | 'dynamical_direct_fallback'
  | 'dynamical_shadow_parity';
